// Government health dashboard (proposal Section 08): service delivery and fleet health in one place,
// role-scoped (facility / district / program; MOVO-X support sees device health only).

import { useCallback, useEffect, useState } from 'react';
import { ApiError, staff, type StaffUser } from '../lib/api.ts';
import type { Realtime } from '../lib/realtime.ts';
import { AjkMap } from '../ui/AjkMap.tsx';
import { ago, Login, StaffHeader, useLive, useStaffSession } from '../ui/staff.tsx';
import { BarChart, ChartFrame, LegendItem, LineChart, Meter } from './charts.tsx';

interface Overview { scope: string; screeningsToday?: number; teleconsultsToday?: number; consultsWaiting?: number; urgentOpen?: number; urgentToday?: number; kiosksTotal: number; kiosksOnline: number }
interface District { id: string; name: string; name_ur: string; division: string; remote: number; facilities: number; kiosks: number; kiosks_online: number; screenings: number; consults: number; urgent: number; open_flags: number }
interface TrendRow { day: string; screenings: number; consults: number; urgent: number }
interface Measures {
  windowDays: number;
  access: { completedSessions: number; teleconsults: number; teleconsultUptake: number };
  flow: { avgSessionMinutes: number | null; avgConsultWaitMinutes: number | null; completionRate: number | null };
  safety: { qualityRetryRate: number; urgentFlags: number; unresolvedFlags: number; avgUrgentResponseMinutes: number | null; clinicianOverrides: number };
  equity: { bySex: { sex: string; n: number }[]; byFacilityType: { type: string; n: number }[]; assistedShare: number };
  risk: { rule: string; severity: string; n: number }[];
}
interface Kiosk { id: string; serial: string; facility: string; facility_type: string; district: string; last_heartbeat: string | null; network: string | null; printer_paper: string | null; power: string | null; queue_depth: number; app_version: string | null; online: number; alerts: string[]; sensors: Record<string, string> }
interface Flag { id: string; encounter_id: string; rule: string; severity: string; message: string; value: number | null; created_at: string; facility: string; district: string }

// Proposal Section 13.4: three-year cost of ownership per kiosk, ~300 operating days a year.
const COST_PER_KIOSK_DAY_USD = 3080 / (3 * 300);

const WINDOWS = [7, 14, 30] as const;

export function DashboardApp() {
  const session = useStaffSession();
  if (session.checking) return <div className="center muted">Loading…</div>;
  if (!session.user) return <Login surface="dashboard" title="AJK program dashboard" onLogin={session.setUser} />;
  return <Dashboard user={session.user} rt={session.rt!} onLogout={session.logout} />;
}

function Dashboard({ user, rt, onLogout }: { user: StaffUser; rt: Realtime; onLogout: () => void }) {
  const live = useLive(rt);
  const support = user.role === 'support';
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(14);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [trend, setTrend] = useState<TrendRow[]>([]);
  const [measures, setMeasures] = useState<Measures | null>(null);
  const [fleet, setFleet] = useState<Kiosk[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [updated, setUpdated] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [o, f] = await Promise.all([staff.get<Overview>('/api/dashboard/overview'), staff.get<Kiosk[]>('/api/dashboard/fleet')]);
      setOverview(o);
      setFleet(f);
      if (!support) {
        const [d, t, m, fl] = await Promise.all([
          staff.get<District[]>(`/api/dashboard/districts?days=${days}`),
          staff.get<TrendRow[]>(`/api/dashboard/trend?days=${days}`),
          staff.get<Measures>('/api/dashboard/measures?days=90'),
          staff.get<Flag[]>('/api/flags'),
        ]);
        setDistricts(d);
        setTrend(t);
        setMeasures(m);
        setFlags(fl);
      }
      setUpdated(new Date().toISOString());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onLogout();
    }
  }, [days, onLogout, support]);

  useEffect(() => {
    void load();
    let pending: number | null = null;
    // Live events trigger a debounced refresh so a busy network does not flood the API.
    const off = rt.on((m) => {
      if (m.type !== 'event') return;
      if (pending) return;
      pending = window.setTimeout(() => {
        pending = null;
        void load();
      }, 1500);
    });
    const iv = setInterval(load, 60000);
    return () => {
      off();
      clearInterval(iv);
      if (pending) clearTimeout(pending);
    };
  }, [load, rt]);

  const scopeLabel = user.role === 'program' ? 'Whole program · all ten districts' : user.role === 'district' ? `District roll-up · ${user.district}` : user.role === 'facility' ? `Facility · ${user.facility}` : user.role === 'clinician' ? 'Clinician view' : 'Device health only (no clinical content)';

  return (
    <div className="staff dashboard viz-root">
      <StaffHeader title="AJK Smart Health Kiosk Program" user={user} onLogout={onLogout} live={live} />
      <div className="d-filters">
        <span className="scope">{scopeLabel}</span>
        {!support && (
          <div className="seg small-seg" role="group" aria-label="Time window">
            {WINDOWS.map((w) => <button key={w} className={`seg-btn ${days === w ? 'on' : ''}`} onClick={() => setDays(w)}>Last {w} days</button>)}
          </div>
        )}
        <span className="muted small">Updated {updated ? ago(updated) : '…'} · synthetic demo data</span>
      </div>

      {overview && <Tiles o={overview} support={support} />}

      <div className="d-map"><AjkMap stats={support ? undefined : districts} windowDays={days} title="Program map — Azad Jammu & Kashmir" /></div>

      {!support && (
        <div className="d-grid">
          <ChartFrame
            title={`Screenings by district — last ${days} days`}
            subtitle="Completed kiosk screenings. Hover a bar for consults, urgent flags and kiosk status."
            table={<DistrictTable rows={districts} />}
          >
            <BarChart unit="screenings" data={[...districts].sort((a, b) => b.screenings - a.screenings).map((d) => ({
              key: d.id, label: d.name, value: d.screenings,
              detail: [`${d.consults} teleconsults · ${d.urgent} urgent flags`, `${d.kiosks_online}/${d.kiosks} kiosks online · ${d.division} division${d.remote ? ' · remote' : ''}`],
            }))} />
          </ChartFrame>
          <ChartFrame
            title="Daily activity"
            subtitle={`Screenings and teleconsults per day, last ${days} days`}
            legend={<><LegendItem color="var(--series-1)" label="Screenings" /><LegendItem color="var(--series-2)" label="Teleconsults" /></>}
            table={<TrendTable rows={trend} />}
          >
            <LineChart x={trend.map((t) => t.day)} series={[
              { key: 's', label: 'Screenings', color: 'var(--series-1)', values: trend.map((t) => t.screenings) },
              { key: 'c', label: 'Teleconsults', color: 'var(--series-2)', values: trend.map((t) => t.consults) },
            ]} />
          </ChartFrame>
        </div>
      )}

      {!support && measures && <PilotMeasures m={measures} fleet={fleet} trend={trend} />}

      {!support && <FlagsPanel flags={flags} user={user} onResolved={load} />}

      <FleetPanel fleet={fleet} />

      {!support && <ExportsPanel user={user} />}
    </div>
  );
}

function Tile({ label, value, sub, hero, tone }: { label: string; value: string; sub?: string; hero?: boolean; tone?: 'critical' | 'warning' }) {
  return (
    <div className={`tile-stat ${hero ? 'hero' : ''}`}>
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      {sub && <div className={`tile-sub ${tone ?? ''}`}>{tone === 'critical' ? '▲ ' : ''}{sub}</div>}
    </div>
  );
}

function Tiles({ o, support }: { o: Overview; support: boolean }) {
  const offline = o.kiosksTotal - o.kiosksOnline;
  return (
    <div className="tiles">
      {!support && <Tile hero label="Screenings today" value={(o.screeningsToday ?? 0).toLocaleString()} />}
      {!support && <Tile label="Teleconsults today" value={(o.teleconsultsToday ?? 0).toLocaleString()} sub={`${o.consultsWaiting ?? 0} waiting for a doctor`} />}
      {!support && <Tile label="Urgent flags open" value={(o.urgentOpen ?? 0).toLocaleString()} sub={`${o.urgentToday ?? 0} raised today`} tone={(o.urgentOpen ?? 0) > 0 ? 'critical' : undefined} />}
      <Tile hero={support} label="Kiosks online" value={`${o.kiosksOnline}/${o.kiosksTotal}`} sub={offline ? `${offline} offline` : 'all reporting'} tone={offline ? 'warning' : undefined} />
    </div>
  );
}

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
const mins = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toFixed(1)} min`);

function PilotMeasures({ m, fleet, trend }: { m: Measures; fleet: Kiosk[]; trend: TrendRow[] }) {
  const uptime = fleet.length ? fleet.filter((k) => k.online).length / fleet.length : 0;
  const female = m.equity.bySex.find((x) => x.sex === 'female')?.n ?? 0;
  const total = m.equity.bySex.reduce((a, x) => a + x.n, 0);
  // Economics (Section 12): cost per completed screening at the three-year cost of ownership,
  // over the selected window (operating days only: six a week).
  const windowScreenings = trend.reduce((a, t) => a + t.screenings, 0);
  const kioskDays = fleet.length * trend.length * (6 / 7);
  const costPerScreening = windowScreenings ? (kioskDays * COST_PER_KIOSK_DAY_USD) / windowScreenings : null;
  const rows: [string, [string, string][]][] = [
    ['Access', [['Completed screenings', m.access.completedSessions.toLocaleString()], ['Teleconsults', m.access.teleconsults.toLocaleString()], ['Teleconsult uptake', pct(m.access.teleconsultUptake)]]],
    ['Flow', [['Average screening time', mins(m.flow.avgSessionMinutes)], ['Average wait for doctor', mins(m.flow.avgConsultWaitMinutes)], ['Session completion', pct(m.flow.completionRate)]]],
    ['Safety', [['Quality retry rate', pct(m.safety.qualityRetryRate)], ['Urgent flags', m.safety.urgentFlags.toLocaleString()], ['Unresolved flags', m.safety.unresolvedFlags.toLocaleString()], ['Urgent response time', mins(m.safety.avgUrgentResponseMinutes)], ['Clinician overrides (with reason)', m.safety.clinicianOverrides.toLocaleString()]]],
    ['Reliability', [['Kiosks reporting now', pct(uptime)], ['Kiosks with alerts', fleet.filter((k) => k.alerts.length).length.toString()]]],
    ['Equity', [['Women screened', pct(total ? female / total : 0)], ['Staff-assisted sessions', pct(m.equity.assistedShare)], ...m.equity.byFacilityType.map((t) => [`At ${t.type}s`, t.n.toLocaleString()] as [string, string])]],
    ['Economics', [['Cost per completed screening', costPerScreening ? `USD ${costPerScreening.toFixed(2)}` : '—'], ['Basis', `USD 3,080 / kiosk / 3 yrs · last ${trend.length} days`]]],
  ];
  return (
    <section className="panel">
      <div className="viz-head">
        <div>
          <h3>Pilot measures — day-90 review</h3>
          <div className="muted small">The seven dimensions agreed in proposal Section 12, computed from live data over the last {m.windowDays} days. Targets are set at initiation.</div>
        </div>
      </div>
      <div className="measures">
        {rows.map(([dim, items]) => (
          <div key={dim} className="measure-card">
            <h4>{dim}</h4>
            <dl>{items.map(([k, v]) => <div key={k} className="kv"><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
            {dim === 'Equity' && <Meter value={total ? female / total : 0} label="Share of screenings by women" />}
          </div>
        ))}
        <div className="measure-card">
          <h4>Most frequent flags</h4>
          <table className="table small">
            <tbody>{m.risk.slice(0, 7).map((r) => <tr key={r.rule}><td>{r.severity === 'urgent' ? '▲ ' : '● '}{r.rule.replace('SYMPTOM_RED_FLAG:', 'Red-flag symptom: ').replace(/_/g, ' ').toLowerCase()}</td><td className="num">{r.n.toLocaleString()}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const DISPOSITIONS = [
  { id: 'seen_by_staff', label: 'Seen by staff' },
  { id: 'referred', label: 'Referred' },
  { id: 'repeat_measurement_normal', label: 'Repeat measurement normal' },
  { id: 'false_reading', label: 'False / faulty reading' },
  { id: 'patient_left', label: 'Patient left' },
];

function FlagsPanel({ flags, user, onResolved }: { flags: Flag[]; user: StaffUser; onResolved: () => void }) {
  const canResolve = ['facility', 'district', 'clinician'].includes(user.role);
  const [open, setOpen] = useState<string | null>(null);
  const [disp, setDisp] = useState('seen_by_staff');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  return (
    <section className="panel">
      <h3>Flags awaiting disposition <span className="muted small">({flags.length})</span></h3>
      <p className="muted small">Urgent or unresolved flags cannot be dismissed by the patient. Authorised staff record the disposition and the reason; every action is audited.</p>
      {flags.length === 0 ? <p className="muted">No open flags.</p> : (
        <table className="table">
          <thead><tr><th>Severity</th><th>Finding</th><th>Facility</th><th>District</th><th>Raised</th>{canResolve && <th />}</tr></thead>
          <tbody>
            {flags.slice(0, 30).map((f) => (
              <tr key={f.id}>
                <td><span className={`status status-${f.severity === 'urgent' ? 'critical' : 'warning'}`}>{f.severity === 'urgent' ? '▲ Urgent' : '● Review'}</span></td>
                <td>{f.message}{f.value !== null ? ` (${f.value})` : ''}</td>
                <td>{f.facility}</td>
                <td>{f.district}</td>
                <td className="muted">{ago(f.created_at)}</td>
                {canResolve && (
                  <td>
                    {open === f.id ? (
                      <div className="row wrap">
                        <select className="input" value={disp} onChange={(e) => setDisp(e.target.value)}>{DISPOSITIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}</select>
                        <input className="input" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                        <button className="btn btn-small btn-primary" disabled={reason.trim().length < 3} onClick={async () => {
                          try {
                            await staff.post(`/api/flags/${f.id}/resolve`, { disposition: disp, reason });
                            setOpen(null);
                            setReason('');
                            onResolved();
                          } catch (e) {
                            setError(e instanceof Error ? e.message : String(e));
                          }
                        }}>Save</button>
                      </div>
                    ) : <button className="btn btn-small" onClick={() => setOpen(f.id)}>Record disposition</button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function kioskStatus(k: Kiosk): { cls: string; label: string } {
  if (!k.online) return { cls: 'critical', label: '✕ Offline' };
  if (k.alerts.length) return { cls: 'warning', label: '! Attention' };
  return { cls: 'good', label: '✓ Online' };
}

function FleetPanel({ fleet }: { fleet: Kiosk[] }) {
  return (
    <section className="panel">
      <h3>Device fleet <span className="muted small">· heartbeat, calibration and printer-paper alerts</span></h3>
      <div className="table-scroll">
        <table className="table">
          <thead><tr><th>Status</th><th>Kiosk</th><th>Facility</th><th>District</th><th>Last heartbeat</th><th>Network</th><th>Power</th><th>Sync queue</th><th>Alerts</th></tr></thead>
          <tbody>
            {fleet.map((k) => {
              const st = kioskStatus(k);
              return (
                <tr key={k.id}>
                  <td><span className={`status status-${st.cls}`}>{st.label}</span></td>
                  <td><code>{k.id}</code><div className="muted small">{k.serial}</div></td>
                  <td>{k.facility}</td>
                  <td>{k.district}</td>
                  <td className="muted">{ago(k.last_heartbeat)}</td>
                  <td>{k.network ?? '—'}</td>
                  <td>{k.power ?? '—'}</td>
                  <td className="num">{k.queue_depth}</td>
                  <td>{k.alerts.join(', ') || <span className="muted">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ExportsPanel({ user }: { user: StaffUser }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [audit, setAudit] = useState<{ ok: boolean; entries: number; head: string } | null>(null);
  const [thresholds, setThresholds] = useState<{ version: string; status: string; approvedBy?: string } | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    void staff.get<{ version: string; status: string; approvedBy?: string }>('/api/config/thresholds').then(setThresholds);
    if (user.role === 'program') void staff.get<{ ok: boolean; entries: number; head: string }>('/api/audit/verify').then(setAudit);
  }, [user.role]);

  const download = async (format: 'json' | 'csv') => {
    setMsg('');
    const res = await fetch(`/api/export/dhis2?period=${period}${format === 'csv' ? '&format=csv' : ''}`, { headers: { authorization: `Bearer ${staff.token()}` } });
    if (!res.ok) return setMsg(`Export failed (${res.status})`);
    const blob = format === 'csv' ? await res.blob() : new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `movox-ajk-dhis2-${period}.${format}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <section className="panel">
      <h3>Reporting, integration &amp; governance</h3>
      <div className="measures">
        <div className="measure-card">
          <h4>DHIS-compatible export</h4>
          <p className="muted small">Aggregate dataValueSets per facility (JSON for the DHIS2 API, CSV for import). Org-unit and data-element IDs are mapped during HIS integration (gate G2).</p>
          <div className="row wrap">
            <input className="input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM or YYYY-MM-DD" />
            <button className="btn btn-small" onClick={() => download('json')}>JSON</button>
            <button className="btn btn-small" onClick={() => download('csv')}>CSV</button>
          </div>
          {msg && <p className="error small">{msg}</p>}
        </div>
        <div className="measure-card">
          <h4>Clinical thresholds</h4>
          {thresholds && <p><code>{thresholds.version}</code> — {thresholds.status === 'approved' ? <span className="status status-good">✓ approved by {thresholds.approvedBy}</span> : <span className="status status-warning">! draft, pending clinical governance sign-off</span>}</p>}
          <p className="muted small">The Department-nominated clinical lead signs off thresholds, triage rules and escalation pathways before go-live.</p>
        </div>
        {audit && (
          <div className="measure-card">
            <h4>Audit trail</h4>
            <p>{audit.ok ? <span className="status status-good">✓ Hash chain intact</span> : <span className="status status-critical">✕ Chain broken</span>} · {audit.entries.toLocaleString()} entries</p>
            <p className="muted small">Head <code>{audit.head.slice(0, 16)}…</code> — tamper-evident, append-only.</p>
          </div>
        )}
        <div className="measure-card">
          <h4>Data ownership</h4>
          <p className="muted small">All program data is owned by the Health Department. MOVO-X processes it only to deliver the service; full export on exit, no lock-in.</p>
        </div>
      </div>
    </section>
  );
}

function DistrictTable({ rows }: { rows: District[] }) {
  return (
    <table className="table">
      <thead><tr><th>District</th><th>Division</th><th className="num">Screenings</th><th className="num">Teleconsults</th><th className="num">Urgent</th><th className="num">Open flags</th><th className="num">Kiosks online</th></tr></thead>
      <tbody>{rows.map((d) => <tr key={d.id}><td>{d.name} <span className="muted" lang="ur">{d.name_ur}</span></td><td>{d.division}</td><td className="num">{d.screenings}</td><td className="num">{d.consults}</td><td className="num">{d.urgent}</td><td className="num">{d.open_flags}</td><td className="num">{d.kiosks_online}/{d.kiosks}</td></tr>)}</tbody>
    </table>
  );
}

function TrendTable({ rows }: { rows: TrendRow[] }) {
  return (
    <table className="table">
      <thead><tr><th>Day</th><th className="num">Screenings</th><th className="num">Teleconsults</th><th className="num">Urgent flags</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.day}><td>{r.day}</td><td className="num">{r.screenings}</td><td className="num">{r.consults}</td><td className="num">{r.urgent}</td></tr>)}</tbody>
    </table>
  );
}
